import { Children, isValidElement, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import Icon from '@/components/public/ui/Icon';
import { pickerTriggerClass } from '@/lib/formStyles';

interface SelectProps {
  id?: string;
  name?: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLSelectElement>;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

type Opt = { value: string; label: string; disabled?: boolean };

// Read <option> children so call-sites keep the familiar native-select API.
function extractOptions(children: ReactNode): Opt[] {
  const opts: Opt[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const props = child.props as {
      value?: string | number;
      disabled?: boolean;
      children?: ReactNode;
    };
    const value = String(props.value ?? '');
    const label =
      typeof props.children === 'string'
        ? props.children
        : typeof props.children === 'number'
          ? String(props.children)
          : '';
    opts.push({ value, label, disabled: props.disabled });
  });
  return opts;
}

// Custom listbox styled to match DatePicker/TimePicker exactly (trigger via
// pickerTriggerClass, `surface-card-elevated` popover). Replaces the native
// <select> whose option list the OS renders unstyled. Keyboard accessible.
export default function Select({
  id,
  name,
  value,
  onChange,
  required,
  disabled,
  className = '',
  children,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: SelectProps) {
  const all = extractOptions(children);
  const placeholder = all.find((o) => o.value === '')?.label ?? 'Selecionar';
  const options = all.filter((o) => o.value !== '');

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const listId = `${reactId}-list`;
  const optionId = (i: number) => `${reactId}-opt-${i}`;

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const emit = (v: string) =>
    onChange({ target: { value: v, name } } as unknown as React.ChangeEvent<HTMLSelectElement>);

  const choose = (opt: Opt) => {
    if (opt.disabled) return;
    emit(opt.value);
    setOpen(false);
  };

  const openList = () => {
    if (disabled) return;
    const idx = options.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    setOpen(true);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const opt = options[activeIndex];
      if (opt) choose(opt);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-required={required}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        className={pickerTriggerClass({ open, hasError: ariaInvalid, disabled })}
      >
        <span className={selected ? '' : 'text-on-surface-light/60'}>
          {selected ? selected.label : placeholder}
        </span>
        <Icon name="expand_more" size="sm" className="text-on-surface-light flex-shrink-0" />
      </button>

      {open && (
        <ul
          role="listbox"
          id={listId}
          className="surface-card-elevated absolute left-0 z-50 mt-2 max-h-60 w-full overflow-auto p-1"
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            const isActive = i === activeIndex;
            return (
              <li key={opt.value + i} role="option" id={optionId(i)} aria-selected={isSelected}>
                <button
                  type="button"
                  disabled={opt.disabled}
                  onClick={() => choose(opt)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    isSelected
                      ? 'bg-primary font-semibold text-white'
                      : isActive
                        ? 'bg-primary/10 text-on-surface'
                        : 'text-on-surface'
                  } ${opt.disabled ? 'cursor-not-allowed opacity-40' : ''}`}
                >
                  {opt.label || '—'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
